import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { fetchSessionsToday } from '../lib/api/sessions';
import { fetchLiveAgents } from '../lib/api/activity';
import type { SessionSummary } from '../../../shared/types/sessions';
import type { LiveAgentActivity } from '../../../shared/types/epics';
import { PageHeader, StatusBadge, type StatusTone } from '../components/ui';
import { useInbox } from '../lib/hooks/useInbox';
import type { InboxItem, InboxCategory } from '../lib/hooks/useInbox';
import { sessionLogsHref, taskIdFromArn } from '../lib/sessions/links';

const POLL_INTERVAL_MS = 15_000;

/** Format a duration in ms as e.g. "1m 23s" or "12s". `null` → live tick "running". */
function formatDuration(ms: number | null, startedAt: string, isRunning: boolean): string {
  const totalMs = ms ?? (isRunning ? Date.now() - new Date(startedAt).getTime() : 0);
  if (!Number.isFinite(totalMs) || totalMs <= 0) return '—';
  const totalSec = Math.floor(totalMs / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatCost(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

function formatTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function buildLogsUrl(session: SessionSummary): string | null {
  // UX-04: shared with the epic drawer's Raw logs tab — see lib/sessions/links.ts.
  if (!session.taskArn) return null;
  return sessionLogsHref({
    task: taskIdFromArn(session.taskArn),
    agent: session.agentType,
    repo: session.repo,
    story: session.storyId,
    epic: session.epicNumber > 0 ? session.epicNumber : undefined,
    epicRef: session.epicRef,
    outcome: session.outcome,
    startedAt: session.startedAt,
    finishedAt: session.finishedAt,
  });
}

const SessionRow: React.FC<{ session: SessionSummary }> = ({ session }) => {
  const {
    agentType, repo, epicNumber, epicRef, storyId, startedAt, finishedAt,
    durationMs, costUsd, iterations, productiveIterations, outcome,
    modelId, isRunning,
  } = session;

  const logsUrl = buildLogsUrl(session);

  return (
    <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
      <td className="px-4 py-3">
        {isRunning ? (
          <StatusBadge tone="inprogress" dot>running</StatusBadge>
        ) : (
          <StatusBadge tone={outcomeTone(outcome)}>{outcome ?? 'finished'}</StatusBadge>
        )}
      </td>
      <td className="px-4 py-3 font-medium text-slate-900">{agentType}</td>
      <td className="px-4 py-3">
        {epicRef ? (
          <Link to={`/epics/${encodeURIComponent(epicRef)}`} className="text-primary-500 hover:text-primary-600 hover:underline font-mono text-xs">
            #{epicNumber > 0 ? epicNumber : 'epic'}
          </Link>
        ) : epicNumber > 0 ? (
          <Link to={`/epics/${epicNumber}`} className="text-primary-500 hover:text-primary-600 hover:underline font-mono text-xs">
            #{epicNumber}
          </Link>
        ) : '—'}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">{repo || '—'}</td>
      <td className="px-4 py-3 text-slate-600">{storyId || '—'}</td>
      <td className="px-4 py-3 tabular-nums font-mono text-xs text-slate-500">{formatTime(startedAt)}</td>
      <td className="px-4 py-3 tabular-nums text-slate-700">{formatDuration(durationMs, startedAt, isRunning)}</td>
      <td className="px-4 py-3 tabular-nums font-mono text-slate-700">{formatCost(costUsd)}</td>
      <td className="px-4 py-3 tabular-nums text-slate-700">
        {productiveIterations}/{iterations}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500" title={modelId}>
        {modelId.replace(/^claude-/, '').replace(/-\d{8}$/, '')}
      </td>
      <td className="px-4 py-3 tabular-nums font-mono text-xs text-slate-500">{finishedAt ? formatTime(finishedAt) : '—'}</td>
      <td className="px-4 py-3 text-right">
        {logsUrl && (
          <Link
            to={logsUrl}
            className="text-xs text-slate-400 hover:text-primary-500 hover:underline whitespace-nowrap"
            title="View CloudWatch logs"
          >
            Logs →
          </Link>
        )}
      </td>
    </tr>
  );
};

function outcomeTone(outcome: string | null): 'success' | 'danger' | 'neutral' {
  if (!outcome) return 'neutral';
  if (outcome === 'task_complete' || outcome === 'COMPLETED') return 'success';
  if (
    outcome === 'crashed' ||
    outcome === 'FAILED' ||
    outcome === 'ERROR' ||
    outcome === 'task_failed' ||
    outcome === 'error'
  ) return 'danger';
  return 'neutral';
}

// ── Today inbox (UX-03) ──────────────────────────────────────────────────
// One merged, deduped, severity-sorted list of everything that needs a
// human — pending work, analytics attention and budget alerts — replacing
// the old Waiting & needs attention board (FORGE-087), which only showed
// pending work. lib/attention/inbox.ts does the merge; useInbox() wraps it.

const CATEGORY_LABEL: Record<InboxCategory, string> = {
  needs_human: 'Needs human',
  review: 'Review',
  failing: 'Failing',
  budget: 'Budget',
};

const CATEGORY_TONE: Record<InboxCategory, StatusTone> = {
  needs_human: 'danger',
  review: 'info',
  failing: 'danger',
  budget: 'warning',
};

const CHIP_ORDER: ReadonlyArray<'all' | InboxCategory> = ['all', 'needs_human', 'failing', 'review', 'budget'];
const CHIP_LABEL: Record<'all' | InboxCategory, string> = {
  all: 'All',
  needs_human: 'Needs human',
  failing: 'Failing',
  review: 'Waiting on review',
  budget: 'Budget',
};

function formatWaiting(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ${mins % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}

const InboxRow: React.FC<{ it: InboxItem }> = ({ it }) => (
  <li data-testid="inbox-item" className="px-4 py-2.5 flex items-start gap-3 text-sm">
    <StatusBadge tone={CATEGORY_TONE[it.category]} dot>
      {CATEGORY_LABEL[it.category]}
    </StatusBadge>
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2">
        <Link to={it.href} className="text-primary-500 hover:underline font-medium truncate">
          {it.title}
        </Link>
        {it.extraReasons && it.extraReasons.length > 0 && (
          <span className="text-xs text-slate-400">· also: {it.extraReasons.join(', ')}</span>
        )}
      </div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">{it.subtitle}</div>
      {it.reason && (
        <div className="text-xs text-slate-600 mt-0.5 truncate" title={it.reason}>{it.reason}</div>
      )}
      {it.runbook && (
        <details className="mt-1">
          <summary className="text-2xs text-slate-500 cursor-pointer select-none">Runbook</summary>
          <code className="block text-2xs font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 mt-1 overflow-x-auto whitespace-pre-wrap">
            {it.runbook}
          </code>
        </details>
      )}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-xs tabular-nums text-slate-500 whitespace-nowrap" title={it.updatedAt || undefined}>
        {formatWaiting(it.ageMinutes)}
      </span>
      <Link
        to={it.href}
        className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline whitespace-nowrap"
      >
        Open
      </Link>
    </div>
  </li>
);

const InboxSection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const includeOlder = searchParams.get('older') === '1';
  const rawFilter = searchParams.get('filter');
  const filter: 'all' | InboxCategory =
    rawFilter && (CHIP_ORDER as readonly string[]).includes(rawFilter)
      ? (rawFilter as 'all' | InboxCategory)
      : 'all';

  const { items, counts, hiddenOlderCount, isLoading, isError } = useInbox(includeOlder);

  const setFilter = (next: 'all' | InboxCategory) => {
    const p = new URLSearchParams(searchParams);
    if (next === 'all') p.delete('filter'); else p.set('filter', next);
    setSearchParams(p, { replace: true });
  };
  const toggleOlder = () => {
    const p = new URLSearchParams(searchParams);
    if (includeOlder) p.delete('older'); else p.set('older', '1');
    setSearchParams(p, { replace: true });
  };

  if (isLoading) return null;
  if (isError) {
    return (
      <div role="alert" className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
        Failed to load your inbox.
      </div>
    );
  }

  const shown = filter === 'all' ? items : items.filter((it) => it.category === filter);

  return (
    <section data-testid="inbox-section" className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter the inbox">
        {CHIP_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`inbox-filter-${key}`}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            className={
              'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ' +
              (filter === key
                ? 'bg-primary-500 border-primary-500 text-white'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50')
            }
          >
            {CHIP_LABEL[key]} {key === 'all' ? counts.all : counts[key]}
          </button>
        ))}
        {(hiddenOlderCount > 0 || includeOlder) && (
          <button
            type="button"
            data-testid="inbox-toggle-older"
            aria-pressed={includeOlder}
            onClick={toggleOlder}
            className="px-2.5 py-1 rounded-full text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
          >
            {includeOlder ? 'Hide older' : 'Show older'}
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div data-testid="inbox-empty" className="text-xs text-slate-500 bg-white border border-slate-200 rounded-xl px-4 py-3">
          Nothing needs you right now.
          {hiddenOlderCount > 0 && ` (${hiddenOlderCount} older item${hiddenOlderCount === 1 ? '' : 's'}.)`}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
          <header className="px-4 py-2.5 border-b border-slate-200 flex items-baseline gap-2">
            <h2 className="text-sm font-semibold text-slate-900">Inbox</h2>
            <span className="text-xs text-slate-500">{shown.length} item{shown.length === 1 ? '' : 's'}</span>
          </header>
          <ul className="divide-y divide-slate-100">
            {shown.map((it) => <InboxRow key={it.id} it={it} />)}
          </ul>
        </div>
      )}
    </section>
  );
};

const SkeletonRow: React.FC = () => (
  <tr className="border-b border-slate-100" aria-hidden="true">
    {Array.from({ length: 11 }).map((_, j) => (
      <td key={j} className="px-4 py-3">
        <div className="h-3 bg-slate-200 rounded animate-pulse" />
      </td>
    ))}
  </tr>
);

/** Seconds → "12s" / "3m" / "1h 5m" (heartbeat age). */
function formatAge(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const LiveAgentRow: React.FC<{ a: LiveAgentActivity }> = ({ a }) => (
  <tr className="border-b border-slate-100 last:border-0">
    <td className="px-4 py-2.5"><StatusBadge tone="inprogress" dot>{a.starting ? 'starting' : 'running'}</StatusBadge></td>
    <td className="px-4 py-2.5 font-medium text-slate-800">{a.agentType}</td>
    <td className="px-4 py-2.5">
      <Link to={`/epics/${encodeURIComponent(a.epicRef)}`} className="text-primary-500 hover:text-primary-600 hover:underline font-mono text-xs">
        #{a.epicNumber}
      </Link>
      {a.epicTitle ? <span className="text-slate-500 ml-1.5">{a.epicTitle}</span> : null}
    </td>
    <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{a.repo || '—'}</td>
    <td className="px-4 py-2.5 text-slate-600">{a.storyId ?? <span className="text-slate-400 italic">epic-level</span>}</td>
    <td className="px-4 py-2.5 text-slate-600">{a.starting ? (a.starting.platform === 'windows' ? 'starting Windows instance' : 'starting') : a.phase}</td>
    <td className="px-4 py-2.5 text-slate-600 tabular-nums">{a.iter != null ? `${a.iter}${a.maxIter != null ? `/${a.maxIter}` : ''}` : '—'}</td>
    <td className="px-4 py-2.5 text-slate-600 tabular-nums">{a.costUsd != null ? formatCost(a.costUsd) : '—'}</td>
    <td className="px-4 py-2.5 text-slate-500 tabular-nums">{formatAge(a.ageSeconds)} ago</td>
  </tr>
);

/**
 * "Running now" — agents actively executing this moment, derived from live
 * heartbeats (not SESSION# rows, which only exist once a run finishes). This is
 * the fix for in-progress agents — especially the epic-planner, which has no
 * story row and so never showed here before. Hidden when nothing is running.
 */
const RunningNowSection: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['activity-live'],
    queryFn: fetchLiveAgents,
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const agents = data?.agents ?? [];
  if (agents.length === 0) return null;

  return (
    <section data-testid="running-now" className="bg-white border border-slate-200 rounded-xl shadow-card overflow-x-auto">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-accent-500 animate-pulse" />
        <h2 className="text-sm font-semibold text-slate-800">Running now</h2>
        <span className="text-xs text-slate-500">{agents.length} agent{agents.length === 1 ? '' : 's'}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            {['Status', 'Agent', 'Epic', 'Repo', 'Story', 'Phase', 'Iters', 'Cost', 'Last beat'].map((h) => (
              <th key={h} className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {agents.map((a, i) => <LiveAgentRow key={`${a.epicRef}-${a.storyId ?? 'epic'}-${a.agentType}-${i}`} a={a} />)}
        </tbody>
      </table>
    </section>
  );
};

export const TodayPage: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['sessions-today'],
    queryFn: fetchSessionsToday,
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const sessions = data ?? [];
  const runningCount = sessions.filter((s) => s.isRunning).length;
  const finishedCount = sessions.length - runningCount;

  return (
    <div data-testid="today-page" className="min-h-screen bg-slate-50">
      <Header title="Today" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <PageHeader
          title="Today"
          description={
            <>
              <span className="font-medium text-slate-800">{sessions.length} sessions</span>
              {' — '}
              <span className="text-accent-600 font-medium">{runningCount} running</span>
              {', '}
              <span>{finishedCount} finished</span>
            </>
          }
        />

        <RunningNowSection />

        <InboxSection />

        {error ? (
          <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
            Failed to load sessions. {(error as Error).message}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Status</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Agent</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Epic</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Repo</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Story</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Started</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Duration</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Cost</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Iters</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Model</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Finished</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                      No agent sessions today.
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => <SessionRow key={s.sessionId} session={s} />)
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default TodayPage;
